// Gateway to the CakePHP backend. Reproduces the old client's contract:
//   GET  -> `?data=<encodeURIComponent(JSON.stringify(value))>` (value may be any JSON value)
//   POST -> JSON body
// Both send cookies (`credentials: "include"`). Auth failures arrive as HTTP 401 or as
// `{ error: ... }` inside a 200; either one sends the browser to the backend login page and
// the returned promise never settles (the page is going away).
import { backendPath } from '$lib/consts.js';
import { toast } from '$state/toast.svelte.js';

export class ApiError extends Error {
	/**
	 * @param {'network' | 'http' | 'parse' | 'update'} kind
	 * @param {string} message
	 * @param {{ status?: number, cause?: unknown }} [extra]
	 */
	constructor(kind, message, extra = {}) {
		super(message);
		this.name = 'ApiError';
		this.kind = kind;
		this.status = extra.status;
		this.cause = extra.cause;
	}
}

/** @type {string | null} */
let knownVersion = null;
let reloading = false;

/** Reset the remembered deploy version (tests). */
export function resetVersion() {
	knownVersion = null;
	reloading = false;
}

/**
 * Every backend response carries `Nutriscript-Version` (the deployed git hash). When it changes
 * under a running session the page reloads once so the user picks up the new client.
 * @param {Response} response
 */
function checkVersion(response) {
	const version = response.headers.get('nutriscript-version');
	if (version === null) return;
	if (knownVersion === null) {
		knownVersion = version;
	} else if (version !== knownVersion && !reloading) {
		reloading = true;
		location.reload();
	}
}

/**
 * Send the browser to the backend login page and come back to the current SPA URL afterwards.
 * The order confirmation / failed pages have no session to recover, so they fall through to the
 * backend's own copies instead.
 * @returns {Promise<never>}
 */
export function redirectToLogin() {
	const path = location.pathname + location.search;
	if (/\/checkout\/confirmation\/?$/.test(location.pathname)) {
		location.replace(backendPath + 'orders/confirmation');
	} else if (/\/checkout\/failed\/?$/.test(location.pathname)) {
		location.replace(backendPath + 'orders/failed');
	} else {
		location.replace(backendPath + 'users/login?redirect=' + encodeURIComponent(path));
	}
	return new Promise(() => {});
}

/**
 * @param {string} url
 * @param {RequestInit} init
 * @returns {Promise<any>}
 */
async function request(url, init) {
	/** @type {Response} */
	let response;
	try {
		response = await fetch(url, {
			referrerPolicy: 'no-referrer',
			credentials: 'include',
			...init
		});
	} catch (e) {
		if (e instanceof Error && e.name === 'AbortError') return null;
		throw new ApiError('network', `Network error calling ${url}`, { cause: e });
	}

	if (response.status === 401) return redirectToLogin();
	if (!response.ok) {
		throw new ApiError('http', `HTTP ${response.status} calling ${url}`, {
			status: response.status
		});
	}

	let json;
	try {
		json = await response.json();
	} catch (e) {
		throw new ApiError('parse', `Bad JSON from ${url}`, { cause: e });
	}

	if (json && typeof json === 'object') {
		if (json.error) return redirectToLogin();
		if (json.update_error) {
			toast.warning(json.update_error);
			throw new ApiError('update', String(json.update_error));
		}
	}

	checkVersion(response);
	return json;
}

/**
 * GET `backendPath + path`. `data` (any JSON value) is sent as `?data=`; omit it to send nothing.
 * @param {string} path e.g. `products/find.json`
 * @param {unknown} [data]
 * @param {{ signal?: AbortSignal }} [options]
 * @returns {Promise<any>}
 */
export function get(path, data, options = {}) {
	let url = backendPath + path;
	if (data !== undefined) {
		url += '?data=' + encodeURIComponent(JSON.stringify(data));
	}
	return request(url, { method: 'GET', signal: options.signal });
}

/**
 * POST a JSON body to `backendPath + path`.
 * @param {string} path
 * @param {unknown} [body]
 * @param {{ signal?: AbortSignal }} [options]
 * @returns {Promise<any>}
 */
export function post(path, body = {}, options = {}) {
	return request(backendPath + path, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify(body),
		signal: options.signal
	});
}
